import { useState, useMemo, useRef } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { FORM_MODE } from "../config/constants";
import { AVAILABILITY_TYPES } from "../config/availability";
import EmptyState from "../components/Shared/EmptyState";
import Modal from "../components/Shared/Modal";
import AvailabilityForm from "../components/Availability/AvailabilityForm";
import AvailabilityDataTable from "../components/Availability/AvailabilityDataTable";
import TimeOffList from "../components/Availability/TimeOff/TimeOffList";
import TimeOffForm from "../components/Availability/TimeOff/TimeOffForm";
import AlertModal from "../components/Shared/AlertModal";
import PrimaryButton from "../components/Shared/PrimaryButton";
import AvailabilityPageSkeleton from "../components/Shared/LoadingSkeletons/AvailabilityPageSkeleton";
import {
  getAvailabilities,
  saveAvailability,
  patchAvailability,
  updateAvailability,
  deleteAvailability,
} from "../api/availabilityApi";
import {
  getTimeOffBlocks,
  createTimeOffBlock,
  updateTimeOffBlock,
  deleteTimeOffBlock,
} from "../api/timeOffApi";
import { useToast } from "../hooks/useToast";
import { useSetupNavigation } from "../hooks/useSetupNavigation";
import useCreateIntent from "../hooks/useCreateIntent";

// UI-only delivery-type grouping. distance + region are both "mobile" (provider
// travels to the client); fixed is "on-site" (clients come to the provider).
// The underlying availabilityType enum and the API payload are unchanged — this
// layer only decides which filter a row falls under. The availability list is a
// single unified list; ALL/MOBILE/FIXED are filters over it (default ALL), not
// separate panes. "Time off" is an orthogonal concept rendered in its own
// section below from its own query/state.
const FILTERS = { ALL: "all", MOBILE: "mobile", FIXED: "fixed", TIME_OFF: "timeOff" };
const groupForType = (type) =>
  type === AVAILABILITY_TYPES.FIXED ? FILTERS.FIXED : FILTERS.MOBILE;

const AvailabilityPage = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [defaultAvailability, setDefaultAvailability] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const modalCloseRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(FILTERS.ALL);
  // Time off has its own drawer + editing state so it never collides with
  // the availability drawer above.
  const [showTimeOffDrawer, setShowTimeOffDrawer] = useState(false);
  const [showTimeOffDeleteModal, setShowTimeOffDeleteModal] = useState(false);
  const [timeOffDeleteModal, setTimeOffDeleteModal] = useState(null);
  const [editingTimeOffBlock, setEditingTimeOffBlock] = useState(null);
  const [isTimeOffFormDirty, setIsTimeOffFormDirty] = useState(false);
  const timeOffModalCloseRef = useRef(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { handleSetupStepComplete, isSetupComplete } = useSetupNavigation();

  const { data: availabilitiesData, isLoading: isLoadingGetAvailabilities } =
    useQuery({
      queryKey: ["availabilities"],
      queryFn: getAvailabilities,
      placeholderData: keepPreviousData,
    });

  const availabilities = useMemo(
    () => availabilitiesData || [],
    [availabilitiesData]
  );

  const { data: timeOffData, isLoading: isLoadingTimeOff } = useQuery({
    queryKey: ["timeOff"],
    queryFn: getTimeOffBlocks,
    placeholderData: keepPreviousData,
  });

  const timeOffBlocks = useMemo(() => timeOffData || [], [timeOffData]);

  // Filter availabilities by type
  const distanceAvailabilities = useMemo(
    () =>
      availabilities.filter(
        (a) => a.availabilityType === AVAILABILITY_TYPES.DISTANCE
      ),
    [availabilities]
  );

  const regionAvailabilities = useMemo(
    () =>
      availabilities.filter(
        (a) => a.availabilityType === AVAILABILITY_TYPES.REGION
      ),
    [availabilities]
  );

  const fixedAvailabilities = useMemo(
    () =>
      availabilities.filter(
        (a) => a.availabilityType === AVAILABILITY_TYPES.FIXED
      ),
    [availabilities]
  );

  // The Mobile tab lists both coverage types together.
  const mobileAvailabilities = useMemo(
    () => [...distanceAvailabilities, ...regionAvailabilities],
    [distanceAvailabilities, regionAvailabilities]
  );

  // Availabilities for the current filter. ALL is the unified list.
  const currentAvailabilities = useMemo(() => {
    switch (activeFilter) {
      case FILTERS.MOBILE:
        return mobileAvailabilities;
      case FILTERS.FIXED:
        return fixedAvailabilities;
      case FILTERS.ALL:
      default:
        return [...mobileAvailabilities, ...fixedAvailabilities];
    }
  }, [activeFilter, mobileAvailabilities, fixedAvailabilities]);

  // Helper to get the display name for a delivery-type group
  const getGroupName = (group) => {
    switch (group) {
      case FILTERS.MOBILE: return "Mobile";
      case FILTERS.FIXED: return "On-site";
      default: return "";
    }
  };

  // Empty-state text, keyed on the active filter.
  const getEmptyStateText = (filter) => {
    switch (filter) {
      case FILTERS.MOBILE:
        return {
          title: "No mobile availability yet",
          description:
            "Add where you travel to clients — a distance from your base, or specific areas.",
        };
      case FILTERS.FIXED:
        return {
          title: "No on-site availability yet",
          description: "Add the address clients come to.",
        };
      default:
        return {
          title: "No availability set up yet",
          description:
            "Tell clients when and where they can book you.",
        };
    }
  };

  const { mutateAsync: mutateSaveAvailability } = useMutation({
    mutationFn: ({ data }) => saveAvailability(data),
    onSuccess: async (_, { availabilityType }) => {
      queryClient.invalidateQueries({
        queryKey: ["availabilities"],
        exact: true,
      });
      closeDrawerHandler();

      if (!isSetupComplete) {
        await handleSetupStepComplete(
          "availability",
          "New availability has been created successfully"
        );
      } else {
        const group = availabilityType ? groupForType(availabilityType) : null;
        toast.success(
          "Availability Created",
          group
            ? `Your ${getGroupName(group)} availability has been added.`
            : "Availability has been created successfully"
        );
      }
    },
  });

  // Table-inline patch (enable/disable toggle) — fire-and-forget.
  const { mutate: mutatePatchAvailability } = useMutation({
    mutationFn: (args) => patchAvailability(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["availabilities"],
        exact: true,
      });
      toast.success("Status Updated", "Availability updated successfully");
    },
    onError: (err) => {
      const errorMessage =
        err.response?.data?.error?.message ||
        "An error occurred while updating availability status";
      toast.error("Operation Failed", errorMessage);
    },
  });

  // Form-driven patch — awaited so AvailabilityForm can handle errors inline.
  const { mutateAsync: mutatePatchAvailabilityAsync } = useMutation({
    mutationFn: (args) => patchAvailability(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["availabilities"],
        exact: true,
      });
      toast.success("Availability Updated", "Availability has been updated successfully");
      closeDrawerHandler();
    },
  });

  const { mutateAsync: mutateUpdateAvailability } = useMutation({
    mutationFn: ({ id, data }) => updateAvailability({ id, data }),
    onSuccess: (_, { availabilityType }) => {
      queryClient.invalidateQueries({
        queryKey: ["availabilities"],
        exact: true,
      });
      toast.success(
        "Availability Updated",
        "Availability has been updated successfully"
      );
      closeDrawerHandler();
    },
  });

  const { mutate: mutateDeleteAvailability } = useMutation({
    mutationFn: (id) => deleteAvailability(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["availabilities"],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: ["setupProgress"],
        exact: true,
      });
      toast.success("Availability Removed", "The availability has been removed");
      // Preserve current tab after delete operations
      // Tab stays the same - no setActiveTab call needed
    },
    onError: (err) => {
      const errorMessage =
        err.response?.data?.error?.message ||
        "An error occurred while deleting availability";
      toast.error("Operation Failed", errorMessage);
      console.error("Error deleting availability:", err);
    },
  });

  // Non-blocking overlap warning shared by create + update — the GET list
  // doesn't return overlappingAppointmentCount, so this toast (fired right
  // after a successful create/update, when the API does return it) is the
  // only place the warning surfaces per §5a.
  const warnIfOverlapping = (block) => {
    if (block?.overlappingAppointmentCount > 0) {
      const n = block.overlappingAppointmentCount;
      toast.warning(
        "Heads up",
        `${n} existing appointment${n === 1 ? "" : "s"} fall in this period.`
      );
    }
  };

  const { mutateAsync: mutateCreateTimeOff } = useMutation({
    mutationFn: (payload) => createTimeOffBlock(payload),
    onSuccess: (block) => {
      queryClient.invalidateQueries({ queryKey: ["timeOff"], exact: true });
      toast.success("Time off added", "Clients can no longer book this period.");
      warnIfOverlapping(block);
      closeTimeOffDrawerHandler();
    },
  });

  const { mutateAsync: mutateUpdateTimeOff } = useMutation({
    mutationFn: ({ id, payload }) => updateTimeOffBlock({ id, payload }),
    onSuccess: (block) => {
      queryClient.invalidateQueries({ queryKey: ["timeOff"], exact: true });
      toast.success("Time off updated", "Your changes have been saved.");
      warnIfOverlapping(block);
      closeTimeOffDrawerHandler();
    },
  });

  const { mutate: mutateDeleteTimeOff } = useMutation({
    mutationFn: (id) => deleteTimeOffBlock(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timeOff"], exact: true });
      toast.success("Time off removed", "That period is bookable again.");
    },
    onError: (err) => {
      const errorMessage =
        err.response?.data?.error?.message ||
        "An error occurred while deleting time off";
      toast.error("Operation Failed", errorMessage);
      console.error("Error deleting time off block:", err);
    },
  });

  const closeDrawerHandler = () => {
    setShowDrawer(false);
    setDefaultAvailability(null);
    setIsFormDirty(false);
  };

  const closeTimeOffDrawerHandler = () => {
    setShowTimeOffDrawer(false);
    setEditingTimeOffBlock(null);
    setIsTimeOffFormDirty(false);
  };

  const editTimeOffHandler = (block) => {
    setEditingTimeOffBlock(block);
    setShowTimeOffDrawer(true);
  };

  // Mobile bottom-nav "+" deep-links here with ?new=availability, opening the
  // availability drawer. ?new=timeoff (if ever emitted directly) opens the
  // time-off drawer instead — Time off is its own section, not a tab, so there
  // is no active-tab context to disambiguate against.
  useCreateIntent(["availability", "timeoff"], (type) => {
    if (type === "timeoff") {
      setActiveFilter(FILTERS.TIME_OFF);
      setShowTimeOffDrawer(true);
    } else {
      setShowDrawer(true);
    }
  });

  const deleteTimeOffHandler = (block) => {
    setTimeOffDeleteModal({
      modalTitle: "Delete time off",
      modalMessage: (
        <>
          Are you sure you want to delete this time off
          {block.reason ? (
            <>
              {" "}(<strong>{block.reason}</strong>)
            </>
          ) : null}
          ? Clients will be able to book this period again.
        </>
      ),
      primaryCaption: "Delete",
      secondaryCaption: "Cancel",
      primaryClickHandler: () => {
        mutateDeleteTimeOff(block._id);
        setShowTimeOffDeleteModal(false);
        setTimeOffDeleteModal(null);
      },
      dismissModalHandler: () => {
        setShowTimeOffDeleteModal(false);
        setTimeOffDeleteModal(null);
      },
    });
    setShowTimeOffDeleteModal(true);
  };

  const deleteAvailabilityHandler = (id) => {
    const selectedAvailability = availabilities.find(
      (availability) => availability._id === id
    );

    if (!selectedAvailability) {
      console.error("Availability not found for deletion:", id);
      return;
    }

    setDeleteModal({
      modalTitle: "Delete availability",
      modalMessage: (
        <>
          Are you sure you want to delete the{" "}
          <strong>{selectedAvailability.name}</strong> availability?
        </>
      ),
      primaryCaption: "Delete",
      secondaryCaption: "Cancel",
      primaryClickHandler: () => {
        mutateDeleteAvailability(id);
        setShowDeleteModal(false);
        setDeleteModal(null);
      },
      dismissModalHandler: () => {
        setShowDeleteModal(false);
        setDeleteModal(null);
      },
    });
    setShowDeleteModal(true);
  };

  const editAvailabilityHandler = (id) => {
    const selectedAvailability = availabilities.find(
      (availability) => availability._id === id
    );

    if (!selectedAvailability) {
      console.error("Availability not found for editing:", id);
      return;
    }

    setDefaultAvailability(selectedAvailability);
    setShowDrawer(true);
  };

  // Filter over the single unified availability list. All/Mobile/On-site are
  // meeting-mode values (where clients meet you); Time off is an orthogonal
  // axis (when you're unavailable), so it's flagged `separated` to render after
  // a divider and never reads as a third meeting-mode.
  const filterList = [
    { name: "All", value: FILTERS.ALL },
    { name: "Mobile", value: FILTERS.MOBILE },
    { name: "On-site", value: FILTERS.FIXED },
    { name: "Time off", value: FILTERS.TIME_OFF, separated: true },
  ];

  const isTimeOffFilter = activeFilter === FILTERS.TIME_OFF;
  const hasContent = availabilities.length > 0 || timeOffBlocks.length > 0;

  if (isLoadingGetAvailabilities) {
    return <AvailabilityPageSkeleton />;
  }

  return (
    <>
      {showDeleteModal && deleteModal ? (
        <AlertModal isOpen={showDeleteModal} {...deleteModal} />
      ) : null}
      {showTimeOffDeleteModal && timeOffDeleteModal ? (
        <AlertModal isOpen={showTimeOffDeleteModal} {...timeOffDeleteModal} />
      ) : null}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center mb-6">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-ink-900">
              Availability
            </h1>
            <p className="mt-2 text-sm text-ink-700">
              Set your hours and where you offer services.
            </p>
          </div>
          {/* Header Add button is filter-aware: "Add time off" on the Time off
              filter, otherwise "Add Availability" (shown once there's content). */}
          {isTimeOffFilter ? (
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              <PrimaryButton
                onClick={() => setShowTimeOffDrawer(true)}
                colour="blue"
                className="inline-flex items-center"
              >
                <PlusIcon aria-hidden="true" className="-ml-0.5 mr-1.5 size-5" />
                Add time off
              </PrimaryButton>
            </div>
          ) : (
            availabilities.length > 0 && (
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <PrimaryButton onClick={() => setShowDrawer(true)} colour="blue" className="inline-flex items-center">
                  <PlusIcon aria-hidden="true" className="-ml-0.5 mr-1.5 size-5" />
                  Add Availability
                </PrimaryButton>
              </div>
            )
          )}
        </div>

        {/* Filter over the unified list. Shown once the provider has any
            availability OR time off — otherwise the zero-state below is the
            setup prompt. */}
        {hasContent && (
          <>
            {/* Mobile: native select. Desktop: segmented pill row. */}
            <div className="grid grid-cols-1 sm:hidden mb-6">
              <select
                value={activeFilter}
                aria-label="Filter availability"
                className="col-start-1 row-start-1 w-full appearance-none bg-none rounded-card bg-white py-2 pl-3 pr-8 text-base text-ink-900 border border-ink-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none"
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                {filterList.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.name}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end fill-ink-400"
              />
            </div>
            <div
              role="tablist"
              aria-label="Filter availability"
              className="hidden sm:inline-flex items-center mb-6 rounded-pill border border-ink-200 bg-cream-50 p-1"
            >
              {filterList.map((f) => (
                <span key={f.value} className="inline-flex items-center">
                  {f.separated && (
                    <span
                      aria-hidden="true"
                      className="mx-1 h-4 w-px bg-ink-200"
                    />
                  )}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeFilter === f.value}
                    onClick={() => setActiveFilter(f.value)}
                    className={
                      activeFilter === f.value
                        ? "rounded-pill bg-accent-500 px-4 py-1.5 text-sm font-medium text-white transition-colors"
                        : `rounded-pill px-4 py-1.5 text-sm font-medium transition-colors hover:text-ink-900 ${
                            f.separated ? "text-ink-400" : "text-ink-500"
                          }`
                    }
                  >
                    {f.name}
                  </button>
                </span>
              ))}
            </div>
          </>
        )}

        {isTimeOffFilter ? (
          isLoadingTimeOff ? (
            <p className="text-sm text-ink-500">Loading…</p>
          ) : (
            <TimeOffList
              blocks={timeOffBlocks}
              onEdit={editTimeOffHandler}
              onDelete={deleteTimeOffHandler}
              onAdd={() => setShowTimeOffDrawer(true)}
            />
          )
        ) : currentAvailabilities.length > 0 ? (
          <AvailabilityDataTable
            availabilities={currentAvailabilities}
            patchAvailability={mutatePatchAvailability}
            deleteAvailability={deleteAvailabilityHandler}
            editAvailability={editAvailabilityHandler}
          />
        ) : (
          // Empty copy tracks the active filter: the generic ALL prompt when
          // there's nothing at all, the filter-specific prompt when a Mobile /
          // On-site filter has hidden everything.
          <EmptyState
            text={getEmptyStateText(activeFilter).title}
            subText={getEmptyStateText(activeFilter).description}
            buttonText="Add Availability"
            onClick={() => setShowDrawer(true)}
          />
        )}

        {/* On the All view, surface Time off contextually — only when there are
            upcoming blocks worth showing. Hidden under Mobile/On-site (pure
            availability) and under the Time off filter (rendered above). */}
        {activeFilter === FILTERS.ALL && timeOffBlocks.length > 0 && (
          <section className="mt-12 border-t border-ink-100 pt-8">
            <div className="mb-6">
              <h2 className="smallcaps text-ink-400">Time off</h2>
              <p className="mt-2 text-sm text-ink-700">
                Dates you're away — clients can't book these periods.
              </p>
            </div>
            <TimeOffList
              blocks={timeOffBlocks}
              onEdit={editTimeOffHandler}
              onDelete={deleteTimeOffHandler}
              onAdd={() => setShowTimeOffDrawer(true)}
            />
          </section>
        )}
      </div>
      {showDrawer ? (
        <Modal
          open={showDrawer}
          onClose={closeDrawerHandler}
          title={
            defaultAvailability
              ? "Edit Availability"
              : "Add Availability"
          }
          size="lg"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
        >
          <AvailabilityForm
            activeTabGroup={
              activeFilter === FILTERS.FIXED ? FILTERS.FIXED : FILTERS.MOBILE
            }
            closeDrawerHandler={closeDrawerHandler}
            saveAvailability={mutateSaveAvailability}
            patchAvailability={mutatePatchAvailabilityAsync}
            updateAvailability={mutateUpdateAvailability}
            defaultAvailability={defaultAvailability}
            mode={defaultAvailability ? FORM_MODE.UPDATE : FORM_MODE.CREATE}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
          />
        </Modal>
      ) : null}
      {showTimeOffDrawer ? (
        <Modal
          open={showTimeOffDrawer}
          onClose={closeTimeOffDrawerHandler}
          title={editingTimeOffBlock ? "Edit time off" : "Add time off"}
          size="lg"
          confirmClose={isTimeOffFormDirty}
          closeRef={timeOffModalCloseRef}
        >
          <TimeOffForm
            defaultBlock={editingTimeOffBlock}
            mode={editingTimeOffBlock ? FORM_MODE.UPDATE : FORM_MODE.CREATE}
            createBlock={mutateCreateTimeOff}
            updateBlock={mutateUpdateTimeOff}
            onCancelHandler={() => timeOffModalCloseRef.current?.()}
            setIsFormDirty={setIsTimeOffFormDirty}
          />
        </Modal>
      ) : null}
    </>
  );
};

export default AvailabilityPage;
